// src/components/character/LicensingSettingsPanel.tsx
// キャラクターのライセンス設定パネル

import React, { useState, useEffect } from 'react';
import {
  DocumentTextIcon,
  CurrencyYenIcon,
  InformationCircleIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import { Character, CharacterLicensing } from '../../types/character';
import { updateLicensingSettings, LicensingSettings } from '../../services/licensingService';

interface LicensingSettingsPanelProps {
  character: Character | null;
  onLicensingUpdated?: (licensing: CharacterLicensing) => void;
}

const LicensingSettingsPanel: React.FC<LicensingSettingsPanelProps> = ({
  character,
  onLicensingUpdated,
}) => {
  const [enabled, setEnabled] = useState(false);
  const [royaltyPercentage, setRoyaltyPercentage] = useState(5);
  const [requiresAttribution, setRequiresAttribution] = useState(true);
  const [customTerms, setCustomTerms] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  // Initialize from character data
  useEffect(() => {
    if (character?.licensing) {
      setEnabled(character.licensing.enabled || false);
      setRoyaltyPercentage(character.licensing.royaltyPercentage || 5);
      setRequiresAttribution(character.licensing.requiresAttribution !== false);
      setCustomTerms(character.licensing.customTerms || '');
    }
  }, [character]);

  const handleChange = () => {
    setHasChanges(true);
    setSuccess(false);
  };

  const handleSave = async () => {
    if (!character) return;

    setIsSaving(true);
    setError(null);
    setSuccess(false);

    try {
      const settings: LicensingSettings = {
        enabled,
        royaltyPercentage,
        requiresAttribution,
        customTerms: customTerms.trim() || undefined,
      };

      const response = await updateLicensingSettings(character.id, settings);

      // Convert to CharacterLicensing and notify parent
      const updatedLicensing: CharacterLicensing = {
        enabled: response.enabled,
        royaltyPercentage: response.royaltyPercentage,
        requiresAttribution: response.requiresAttribution,
        customTerms: response.customTerms,
        updatedAt: response.updatedAt
          ? { toDate: () => new Date(response.updatedAt!) } as any
          : { toDate: () => new Date() } as any,
      };

      onLicensingUpdated?.(updatedLicensing);
      setSuccess(true);
      setHasChanges(false);

      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      console.error('Failed to save licensing settings:', err);
      setError(err instanceof Error ? err.message : '保存に失敗しました');
    } finally {
      setIsSaving(false);
    }
  };

  if (!character) return null;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center gap-2">
        <DocumentTextIcon className="w-4 h-4 text-white/60" />
        <h3 className="text-xs font-medium text-white/60 tracking-wide uppercase">
          ライセンス設定
        </h3>
      </div>

      {/* Info Box */}
      <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-3">
        <div className="flex gap-2">
          <InformationCircleIcon className="w-4 h-4 text-blue-400 flex-shrink-0 mt-0.5" />
          <p className="text-[10px] text-blue-300/80 leading-relaxed">
            ライセンスを有効にすると、他のユーザーがこのキャラクターを使ってFigryneでグッズを作成できます。
            販売時に設定したロイヤリティが自動で支払われます。
            <br />
            <span className="text-blue-400/60">※収益化の承認とStripe Connect登録が必要です</span>
          </p>
        </div>
      </div>

      {/* Enable Toggle */}
      <label className="flex items-center justify-between cursor-pointer">
        <div>
          <p className="text-xs font-medium text-white">ライセンスを有効化</p>
          <p className="text-[9px] text-white/40">
            他のユーザーが二次創作グッズを作成可能に
          </p>
        </div>
        <div
          className={`w-9 h-5 rounded-full p-0.5 transition-colors ${
            enabled ? 'bg-green-500' : 'bg-white/20'
          }`}
          onClick={() => {
            setEnabled(!enabled);
            handleChange();
          }}
        >
          <div
            className={`w-4 h-4 rounded-full bg-white transition-transform ${
              enabled ? 'translate-x-4' : ''
            }`}
          />
        </div>
      </label>

      {enabled && (
        <>
          {/* Royalty Percentage */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-1">
                <CurrencyYenIcon className="w-3 h-3 text-white/40" />
                <p className="text-xs font-medium text-white">ロイヤリティ</p>
              </div>
              <span className="text-sm font-bold text-purple-400">
                {royaltyPercentage}%
              </span>
            </div>
            <input
              type="range"
              min="1"
              max="20"
              value={royaltyPercentage}
              onChange={(e) => {
                setRoyaltyPercentage(parseInt(e.target.value));
                handleChange();
              }}
              className="w-full h-1.5 bg-white/20 rounded-full appearance-none cursor-pointer
                [&::-webkit-slider-thumb]:appearance-none
                [&::-webkit-slider-thumb]:w-4
                [&::-webkit-slider-thumb]:h-4
                [&::-webkit-slider-thumb]:rounded-full
                [&::-webkit-slider-thumb]:bg-purple-500
                [&::-webkit-slider-thumb]:cursor-pointer
                [&::-webkit-slider-thumb]:shadow-lg"
            />
            <div className="flex justify-between text-[8px] text-white/30 mt-1">
              <span>1%</span>
              <span>20%</span>
            </div>
            <p className="text-[9px] text-white/40 mt-1">
              グッズ販売額の{royaltyPercentage}%があなたに支払われます
            </p>
          </div>

          {/* Attribution Toggle */}
          <label className="flex items-center justify-between cursor-pointer">
            <div>
              <p className="text-xs font-medium text-white">クレジット表記を必須</p>
              <p className="text-[9px] text-white/40">
                商品にキャラクター名を表示
              </p>
            </div>
            <div
              className={`w-9 h-5 rounded-full p-0.5 transition-colors ${
                requiresAttribution ? 'bg-green-500' : 'bg-white/20'
              }`}
              onClick={() => {
                setRequiresAttribution(!requiresAttribution);
                handleChange();
              }}
            >
              <div
                className={`w-4 h-4 rounded-full bg-white transition-transform ${
                  requiresAttribution ? 'translate-x-4' : ''
                }`}
              />
            </div>
          </label>

          {/* Custom Terms */}
          <div>
            <p className="text-xs font-medium text-white mb-1">
              カスタム利用規約（任意）
            </p>
            <textarea
              value={customTerms}
              onChange={(e) => {
                setCustomTerms(e.target.value);
                handleChange();
              }}
              placeholder="追加の利用条件があれば入力..."
              rows={2}
              maxLength={1000}
              className="w-full px-3 py-2 bg-white/10 text-white text-[10px] rounded-lg placeholder-white/30 outline-none focus:bg-white/15 transition-colors resize-none"
            />
            <p className="text-[8px] text-white/30 text-right mt-0.5">
              {customTerms.length}/1000
            </p>
          </div>
        </>
      )}

      {/* Error Message */}
      {error && (
        <div className="flex items-center gap-2 p-2 bg-red-500/20 rounded-lg">
          <ExclamationTriangleIcon className="w-4 h-4 text-red-400 flex-shrink-0" />
          <p className="text-[10px] text-red-300">{error}</p>
        </div>
      )}

      {/* Success Message */}
      {success && (
        <div className="flex items-center gap-2 p-2 bg-green-500/20 rounded-lg">
          <CheckCircleIcon className="w-4 h-4 text-green-400 flex-shrink-0" />
          <p className="text-[10px] text-green-300">設定を保存しました</p>
        </div>
      )}

      {/* Save Button */}
      {hasChanges && (
        <button
          onClick={handleSave}
          disabled={isSaving}
          className="w-full py-2 bg-purple-600 hover:bg-purple-700 disabled:bg-purple-600/50 text-white text-xs font-medium rounded-lg transition-colors flex items-center justify-center gap-2"
        >
          {isSaving ? (
            <>
              <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
              保存中...
            </>
          ) : (
            'ライセンス設定を保存'
          )}
        </button>
      )}
    </div>
  );
};

export default LicensingSettingsPanel;
